import { GRID_SESSION_KEY_PREFIX } from '@cdw-ae/common-ui-components-search-grid';
import { buildSsrmLockStorageKey } from '@cdw-ae/jobs-status-state';

/**
 * Shared utilities for clearing grid-related state at app lifecycle events.
 * Used by EDUS-2568 (device selection clearing) and EDUS-2702 (column sort clearing).
 */

/** Keys inside a persisted column entry that carry sort state, and nothing else. */
const SORT_STATE_FIELDS = ['sort', 'sortIndex'] as const;

const COMPONENT_STATE_PREFIX = 'cdw_component_state_';

/**
 * Clear all grid session UUIDs from sessionStorage.
 * Grid session UUIDs coordinate server-authoritative selection state (Redis).
 * Called at three lifecycle points: app init, login, and logout.
 *
 * Skips a grid session that still has a matching `ssrm-lock:{uuid}` entry —
 * that means a bulk job is in flight for it, and `DeviceSearchComponent`'s
 * pessimistic hydration (EDUS-2528) needs the UUID to survive a same-tab
 * refresh to re-match the lock. Rotating it here would strand the lock entry
 * and stop checkboxes from re-disabling across the refresh.
 */
export function clearGridSessions(): void {
  const gridSessionKeys = Object.keys(sessionStorage).filter(key =>
    key.startsWith(GRID_SESSION_KEY_PREFIX)
  );
  gridSessionKeys.forEach(key => {
    const uuid = sessionStorage.getItem(key);
    const hasActiveLock =
      !!uuid && sessionStorage.getItem(buildSsrmLockStorageKey(uuid)) !== null;
    if (!hasActiveLock) {
      sessionStorage.removeItem(key);
    }
  });
}

/**
 * Strip persisted **sort** state from every grid's component-state blob,
 * leaving the rest of the layout (column order, visibility, width, flex) and
 * `pageSize` intact. Called at three lifecycle points: app init (page
 * refresh), login, and logout.
 *
 * EDUS-2702 wanted sort to survive in-app navigation but reset on refresh, and
 * implemented that by deleting the whole `cdw_component_state_*` entry. That
 * entry is a single blob holding sort AND column layout, so it also wiped the
 * user's Column Selector choices on every refresh (EDUS-2902). Rewriting the
 * blob without the sort fields keeps both behaviours: sort resets, layout
 * persists.
 *
 * Dropping the fields (rather than writing `sort: null`) is what makes the
 * reset fall back to the grid's *default* sort from `columnDefs` — AG-Grid's
 * `applyColumnState` leaves sort untouched when the state entry says nothing
 * about it, but an explicit `null` clears it. That pairs with
 * `CdwSsrmGridComponent.restoreColumnState()`, which for the same reason must
 * not pass `defaultState: { sort: null }`.
 *
 * Blobs that aren't parseable JSON, aren't objects, or carry no `columnState`
 * array are left untouched — a legacy or corrupted entry is the grid's problem
 * to reject on read, not this utility's to rewrite.
 */
export function clearPersistedGridSort(): void {
  const componentStateKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith(COMPONENT_STATE_PREFIX)
  );
  componentStateKeys.forEach((key) => {
    const raw = localStorage.getItem(key);
    if (!raw) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (parsed === null || typeof parsed !== 'object') return;

    const blob = parsed as Record<string, unknown>;
    const columnState = blob.columnState;
    if (!Array.isArray(columnState)) return;

    let stripped = false;
    const withoutSort = columnState.map((entry) => {
      if (entry === null || typeof entry !== 'object') return entry;
      const column = { ...(entry as Record<string, unknown>) };
      for (const field of SORT_STATE_FIELDS) {
        if (field in column) {
          delete column[field];
          stripped = true;
        }
      }
      return column;
    });
    if (!stripped) return;

    try {
      localStorage.setItem(
        key,
        JSON.stringify({ ...blob, columnState: withoutSort })
      );
    } catch {
      // Silently fail if localStorage is unavailable / over quota — the stale
      // sort simply survives the refresh; the column layout is unharmed.
    }
  });
}
